

import './animations.css';
import { useMemo, useCallback, memo } from 'react';
import { QuickAction } from './index';
import { Flame, Sparkles, ExternalLink, Moon, Sun } from 'lucide-react';
import { Badge } from '@/src/components/ui/shadcn/badge';
import { cn } from '@/src/lib/shadcn/cn';
import { useRouter } from 'next/navigation'
import { useTheme } from 'next-themes';

interface QuickActionsRenderProps {
  actions: QuickAction[];
  onActionClick: (action: QuickAction) => void;
}

function QuickActionItem({ action, onActionClick }: { action: QuickAction; onActionClick: (action: QuickAction) => void }) {
  const router = useRouter();
  
  const handleActionClick = useCallback(() => {
    if (action.disabled) return;

    if (action.href) {
      onActionClick(action);
      router.push(action.href);
      return;
    }

    if (action.onClick) {
      onActionClick(action);
      return;
    }

    onActionClick(action);
  }, [action, onActionClick, router]);

  const handleNewTabClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (action.newTabHref) {
      window.open(action.newTabHref, '_blank');
      onActionClick(action);
    }
  }, [action, onActionClick]);

  return (
    <button
      key={action.text}
      onClick={handleActionClick}
      className={cn(
        "flex items-start gap-3 w-full text-left px-3 py-2 rounded-lg group",
        "hover:bg-gray-100 dark:hover:bg-gray-700/50",
        "text-gray-700 dark:text-gray-300 text-sm",
        "quick-action-item quick-action-item-visible",
        action.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent"
      )}
    >
      <span className="mt-0.5 text-gray-500 dark:text-gray-400">
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500">
          {action.icon}
        </div>
      </span>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">{action.text}</span>
            {action.badge && (
              <Badge
                variant={action.isPopular ? "default" : "secondary"}
                className={cn(
                  "rounded-md px-2 py-0.5 text-xs font-medium",
                  action.isPopular
                    ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400'
                    : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                )}
              >
                <div className="flex items-center gap-1">
                  {action.isPopular ? (
                    <Flame className="w-3 h-3" />
                  ) : (
                    <Sparkles className="w-3 h-3" />
                  )}
                  {action.badge}
                </div>
              </Badge>
            )}
            {action.comingSoon && !action.badge && (
              <Badge variant="outline" className="rounded-md px-1.5 py-0.5 text-xs">
                Coming soon
              </Badge>
            )}
          </div>
          {action.newTabHref && !action.disabled && (
            <button
              onClick={handleNewTabClick}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
            >
              <ExternalLink className="w-3 h-3 text-gray-500" />
            </button>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">{action.description}</span>
      </div>
    </button>
  );
}

const MemoizedQuickActionItem = memo(QuickActionItem);

export default function QuickActionsRender({
  actions,
  onActionClick
}: QuickActionsRenderProps) {
  const { theme, setTheme } = useTheme();

  if (actions.length === 0) return null;

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);
  
  const memoizedActions = useMemo(() => actions, [actions]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
          Quick Actions
        </h3>
        <div
          className="flex items-center gap-1.5 ml-2 text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={toggleTheme}
        >
          <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
          {theme === 'light' ? (
            <Moon className="w-3.5 h-3.5" />
          ) : (
            <Sun className="w-3.5 h-3.5 text-amber-400" />
          )}
        </div>
        <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {memoizedActions.map((action) => (
          <MemoizedQuickActionItem 
            key={action.text}
            action={action}
            onActionClick={onActionClick}
          />
        ))}
      </div>
    </div>
  );
}